import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { $, prefersReducedMotion } from '../utils/dom';
import { cubicBezier } from '../utils/easing';

// Final resting state shared by every reveal here — used to snap items
// visible under reduced motion instead of running the stagger tween.
const REVEAL_FINAL = { opacity: 1, x: 0, y: 0, scale: 1 };

function initExpandable(containerOrSel, itemSel, moreSel, cols) {
  const container = typeof containerOrSel === 'string' ? $(containerOrSel) : containerOrSel;
  if (!container) return;

  const button = moreSel ? $(`${moreSel} button`) : null;
  const items = container.querySelectorAll(itemSel);
  if (!items.length) return;

  // Classify items: first half of each row comes from left, second half from right
  items.forEach((item, i) => {
    const posInRow = i % cols;
    if (posInRow >= cols / 2) {
      item.classList.add('from-right');
    }
  });

  // Determine how many items are visible before "load more" is clicked
  const visibleRowsDesktop = container.dataset.visibleRows || 2;
  const visibleRowsMobile = container.dataset.visibleRowsMobile || 2;
  const visibleRows = window.innerWidth >= 992 ? visibleRowsDesktop : visibleRowsMobile;
  const hasMore = !!button && cols * visibleRows < items.length;
  const visibleCount = hasMore ? Math.min(cols * visibleRows, items.length) : items.length;

  const visibleItems = Array.from(items).slice(0, visibleCount);
  const hiddenItems = Array.from(items).slice(visibleCount);

  const revealTween = {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    duration: .8,
    ease: cubicBezier(0.22, -0.01, 0.13, 0.99),
    stagger: 0.07,
  };

  // Stagger fade-in on viewport entry — only the visible batch.
  const mm = gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', () => {
    gsap.to(visibleItems, {
      ...revealTween,
      scrollTrigger: {
        trigger: container,
        start: 'top 85%',
      },
    });
  });
  mm.add('(prefers-reduced-motion: reduce)', () => {
    gsap.set(visibleItems, REVEAL_FINAL);
  });

  if (!button) return;

  // Hide button if nothing to reveal
  if (!hasMore) {
    button.parentElement.style.display = 'none';
    return;
  }

  // Clip to visible rows
  // For grids with CSS --visible-rows (logos), set the custom property
  // For flex layouts (items), measure and set max-height directly
  if (container.classList.contains('logos')) {
    container.style.setProperty('--visible-rows', visibleRows);
  } else {
    let maxH = 0;
    for (let i = 0; i < visibleCount; i++) {
      const bottom = items[i].offsetTop + items[i].offsetHeight - container.offsetTop;
      if (bottom > maxH) maxH = bottom;
    }
    container.style.maxHeight = maxH + 'px';
  }

  // Reveal on click
  button.addEventListener('click', () => {
    // Under reduced motion, expand and reveal instantly — no height/stagger tween.
    if (prefersReducedMotion()) {
      container.style.maxHeight = '';
      container.classList.add('is-expanded');
      gsap.set(hiddenItems, REVEAL_FINAL);
      button.parentElement.remove();
      ScrollTrigger.refresh();
      return;
    }

    const fullHeight = container.scrollHeight;

    gsap.to(container, {
      maxHeight: fullHeight,
      duration: 0.8,
      ease: 'power3.inOut',
      onComplete: () => {
        container.classList.add('is-expanded');
        container.style.maxHeight = '';
        ScrollTrigger.refresh();
      },
    });

    // Animate the newly-revealed items with the same stagger
    gsap.to(hiddenItems, revealTween);

    gsap.to(button, {
      opacity: 0,
      y: 10,
      duration: 0.4,
      ease: 'power3.inOut',
      onComplete: () => {
        button.parentElement.remove();
      },
    });
  });
}

export default function initLogos() {
  const logoCols = window.innerWidth >= 992 ? 4 : 2;
  initExpandable('.logos', '.logo', '.logos-more', logoCols);

  const itemCols = window.innerWidth >= 992 ? 2 : 1;
  initExpandable('.about__publications .items', '.about__publications .item', '.about__publications .items-more', itemCols);

  // Team people groups — reuse the same stagger reveal (no expand/load-more)
  document.querySelectorAll('.team .people').forEach((group) => {
    initExpandable(group, '.people__item', null, itemCols);
  });

  // Services cases — reveal each row with the same stagger.
  // Use the row's own item count as cols so the split between
  // from-left / from-right matches the row (works for 2- and 3-item rows).
  document.querySelectorAll('.services__cases .cases').forEach((row) => {
    const count = row.querySelectorAll('.cases__item').length;
    if (!count) return;
    initExpandable(row, '.cases__item', null, count);
  });
}
